import React, { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import RequirePlatformAdmin from '@/components/auth/RequirePlatformAdmin';
import RequireHubAccess from '@/components/auth/RequireHubAccess';
import RequireAppAccess from '@/components/auth/RequireAppAccess';
import RequirePermission from '@/components/auth/RequirePermission';
import RequireAnyPermission from '@/components/auth/RequireAnyPermission';
import { LoadingScreen } from '@/components/LoadingScreen';
import NotFound from '@/pages/NotFound';
import { getAdminNavigationItem } from '@/components/navigation/adminNavigation';
import { isKnownHomeSection } from './homeSectionVisibility';

export { isKnownHomeSection } from './homeSectionVisibility';

const Wallet = lazy(() => import('@/features/business/pages/WalletPage'));
const EnhancedDashboard = lazy(() => import('@/features/business/pages/FinancialDashboardPage'));
const PersonalePage = lazy(() => import('@/features/business/pages/PersonalePage'));
const AdminHome = lazy(() => import('@/features/admin/pages/AdminHomePage'));
const MaintenanceManagementPage = lazy(() => import('@/features/admin/pages/MaintenanceManagementPage'));
const BusinessHome = lazy(() => import('@/features/business/pages/BusinessHomePage'));
const DependenciesHome = lazy(() => import('@/features/dipendenze/pages/DependenciesHomePage'));
const DocumentsHubHome = lazy(() => import('@/features/documents/pages/DocumentsHubHomePage'));
const DocumentsHome = lazy(() => import('@/features/documents/pages/DocumentsHomePage'));
const DocumentDetail = lazy(() => import('@/features/documents/pages/DocumentDetailPage'));
const DocumentsIntake = lazy(() => import('@/features/documents/pages/DocumentIntakeConsolePage'));
const DocumentsManager = lazy(() => import('@/features/documents/components/DocumentManagerConsole'));
const DocumentsLinks = lazy(() => import('@/features/documents/pages/DocumentsLinksPage'));
const DocumentsConfiguration = lazy(() => import('@/features/documents/pages/DocumentsConfigurationPage'));
const TaxonomyConsole = lazy(() => import('@/features/documents/components/TaxonomyConsole'));
const DocumentTypeConsole = lazy(() => import('@/features/documents/components/DocumentTypeConsole'));
const DependenciesDashboard = lazy(() => import('@/features/dipendenze/pages/DependenciesDashboardPage'));
const PianiTerapeuticiPage = lazy(() => import('@/features/dipendenze/pages/piani-terapeutici/PianiTerapeuticiHomePage'));
const AccessControlPage = lazy(() => import('@/features/admin/AccessControlPage'));
const CaricamentiPage = lazy(() => import('@/features/admin/pages/CaricamentiPage'));
const DataImportCenterPage = lazy(() => import('@/features/admin/data-import/DataImportCenterPage'));
const CaricamentoGISSSPage = lazy(() => import('@/features/admin/pages/CaricamentoGISSSPage'));
const SyncUsersPage = lazy(() => import('@/features/admin/pages/SyncUsersPage'));
const NotifichePage = lazy(() => import('@/features/admin/pages/NotifichePage'));
const SicurezzaPage = lazy(() => import('@/features/admin/pages/SicurezzaPage'));
const PianiPage = lazy(() => import('@/features/dipendenze/pages/piani-terapeutici/PianiPage'));
const QuestionariPage = lazy(() => import('@/features/dipendenze/pages/piani-terapeutici/QuestionariPage'));
const PianiCaricaDatiPage = lazy(() => import('@/features/dipendenze/pages/piani-terapeutici/CaricaDatiPage'));
const AssistenteIAPage = lazy(() => import('@/features/dipendenze/pages/piani-terapeutici/AssistenteIAPage'));
const GestioneAssistitiPage = lazy(() => import('@/features/dipendenze/pages/GestioneAssistitiPage'));
const GestioneStrutturePage = lazy(() => import('@/features/dipendenze/pages/GestioneStrutturePage'));
const ContattiUtiliPage = lazy(() => import('@/features/dipendenze/pages/ContattiUtiliPage'));
const AreaSanitariaPage = lazy(() => import('@/features/dipendenze/pages/AreaSanitariaPage'));
const GestioneTerapiaPage = lazy(() => import('@/features/dipendenze/pages/GestioneTerapiaPage'));
const GestioneMMGPage = lazy(() => import('@/features/dipendenze/pages/GestioneMMGPage'));
const GestioneOrdiniPage = lazy(() => import('@/features/dipendenze/pages/GestioneOrdiniPage'));
const GestioneStdpPage = lazy(() => import('@/features/dipendenze/pages/GestioneStdpPage'));
const ApplicativiHome = lazy(() => import('@/features/applicativi/pages/ApplicativiHomePage'));
const SupplierRegistryPage = lazy(() => import('@/features/suppliers/pages/SupplierRegistryPage'));
const SupplierImportPage = lazy(() => import('@/features/suppliers/pages/SupplierImportPage'));
const HRHomePage = lazy(() => import('@/features/hr/pages/HRHomePage'));
const SociDipendentiPage = lazy(() => import('@/features/hr/pages/SociDipendentiPage'));
const CedoliniPage = lazy(() => import('@/features/hr/pages/CedoliniPage'));
const InailPage = lazy(() => import('@/features/hr/pages/InailPage'));
const ParametriPage = lazy(() => import('@/features/hr/pages/ParametriPage'));
const TurniPage = lazy(() => import('@/features/hr/turni/pages/TurniPage'));
const HrCaricaDatiPage = lazy(() => import('@/features/hr/pages/CaricaDatiPage'));

function legacyDocumentTarget(section: string): string | null {
  if (section === 'documenti' || section === 'documents/console') return '/documents';
  if (section === 'documents/console/intake' || section === 'documents/console/documents/new') {
    return '/documents/acquisition';
  }
  if (section === 'documents/console/documents') return '/documents/manage';
  if (section === 'documents/console/taxonomies') return '/documents/configuration/taxonomies';
  if (section === 'documents/console/document-types') return '/documents/configuration/document-types';
  const intake = section.match(/^documents\/console\/intake\/([0-9a-f-]{36})$/i);
  if (intake) return `/documents/acquisition/${intake[1]}`;
  const edit = section.match(/^documents\/console\/documents\/([0-9a-f-]{36})$/i);
  if (edit) return `/documents/${edit[1]}?edit=1`;
  return null;
}

type RenderHomeContentParams = {
  currentSection: string;
  fallbackContent: React.ReactNode;
  isHome: boolean;
};

function withRouteLoader(content: React.ReactNode): React.ReactNode {
  return <Suspense fallback={<LoadingScreen message="Caricamento sezione..." fullScreen={false} />}>{content}</Suspense>;
}

export function renderHomeContent({ currentSection, fallbackContent, isHome }: RenderHomeContentParams): React.ReactNode {
  if (isHome) return fallbackContent;
  if (!isKnownHomeSection(currentSection)) return <NotFound />;

  const legacyTarget = legacyDocumentTarget(currentSection);
  if (legacyTarget) return <Navigate to={legacyTarget} replace />;

  if (currentSection === 'documents') {
    return withRouteLoader(
      <RequireHubAccess hubId="documenti">
        <DocumentsHubHome />
      </RequireHubAccess>,
    );
  }
  if (currentSection === 'documents/catalog') {
    return withRouteLoader(
      <RequireHubAccess hubId="documenti">
        <DocumentsHome />
      </RequireHubAccess>,
    );
  }
  if (currentSection === 'documents/acquisition') {
    return withRouteLoader(
      <RequireAppAccess hubId="documenti" appId="console">
        <RequirePermission permission="feature:documenti.console.documents:update">
          <DocumentsIntake />
        </RequirePermission>
      </RequireAppAccess>,
    );
  }
  const intakeMatch = currentSection.match(/^documents\/acquisition\/([0-9a-f-]{36})$/i);
  if (intakeMatch) {
    return withRouteLoader(
      <RequireAppAccess hubId="documenti" appId="console">
        <RequirePermission permission="feature:documenti.console.documents:update">
          <DocumentsIntake documentId={intakeMatch[1]} />
        </RequirePermission>
      </RequireAppAccess>,
    );
  }
  if (currentSection === 'documents/manage') {
    return withRouteLoader(
      <RequireAppAccess hubId="documenti" appId="console">
        <RequireAnyPermission permissions={[
          'feature:documenti.console.documents:update',
          'feature:documenti.console.documents:manage',
        ]}>
          <DocumentsManager />
        </RequireAnyPermission>
      </RequireAppAccess>,
    );
  }
  if (currentSection === 'documents/links') {
    return withRouteLoader(
      <RequireAppAccess hubId="documenti" appId="console">
        <RequireAnyPermission permissions={[
          'feature:documenti.console.documents:update',
          'feature:documenti.console.documents:manage',
        ]}>
          <DocumentsLinks />
        </RequireAnyPermission>
      </RequireAppAccess>,
    );
  }
  if (currentSection === 'documents/configuration') {
    return withRouteLoader(
      <RequireAppAccess hubId="documenti" appId="console">
        <RequireAnyPermission permissions={[
          'feature:documenti.console.taxonomies:view',
          'feature:documenti.console.document-types:view',
        ]}>
          <DocumentsConfiguration />
        </RequireAnyPermission>
      </RequireAppAccess>,
    );
  }
  if (currentSection === 'documents/configuration/taxonomies') {
    return withRouteLoader(
      <RequireAppAccess hubId="documenti" appId="console">
        <RequirePermission permission="feature:documenti.console.taxonomies:view">
          <TaxonomyConsole />
        </RequirePermission>
      </RequireAppAccess>,
    );
  }
  if (currentSection === 'documents/configuration/document-types') {
    return withRouteLoader(
      <RequireAppAccess hubId="documenti" appId="console">
        <RequirePermission permission="feature:documenti.console.document-types:view">
          <DocumentTypeConsole />
        </RequirePermission>
      </RequireAppAccess>,
    );
  }

  const documentMatch = currentSection.match(/^documents\/([0-9a-f-]{36})$/i);
  if (documentMatch) {
    return withRouteLoader(
      <RequireHubAccess hubId="documenti">
        <DocumentDetail documentId={documentMatch[1]} />
      </RequireHubAccess>
    );
  }

  switch (currentSection) {
    case 'business':
      return withRouteLoader(
        <RequireHubAccess hubId="business">
          <BusinessHome />
        </RequireHubAccess>
      );
    case 'business/dashboard':
    case 'unified-dashboard':
      return withRouteLoader(
        <RequireAppAccess hubId="business" appId="dashboard">
          <EnhancedDashboard />
        </RequireAppAccess>
      );
    case 'business/personale':
    case 'personale':
      return withRouteLoader(
        <RequireAppAccess hubId="business" appId="personale">
          <PersonalePage />
        </RequireAppAccess>
      );
    case 'business/portafoglio':
    case 'wallet':
      return withRouteLoader(
        <RequireAppAccess hubId="business" appId="portafoglio">
          <Wallet />
        </RequireAppAccess>
      );
    case 'business/fornitori':
      return withRouteLoader(
        <RequireAppAccess hubId="business" appId="fornitori">
          <SupplierRegistryPage />
        </RequireAppAccess>
      );
    case 'dipendenze':
      return withRouteLoader(
        <RequireHubAccess hubId="dipendenze">
          <DependenciesHome />
        </RequireHubAccess>
      );
    case 'dipendenze/dashboard':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="dashboard">
          <DependenciesDashboard />
        </RequireAppAccess>
      );
    case 'piani-terapeutici':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="piani-terapeutici">
          <PianiTerapeuticiPage />
        </RequireAppAccess>
      );
    case 'piani-terapeutici/carica-dati':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="pt-carica-dati">
          <PianiCaricaDatiPage />
        </RequireAppAccess>
      );
    case 'piani-terapeutici/questionari':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="pt-questionari">
          <QuestionariPage />
        </RequireAppAccess>
      );
    case 'piani-terapeutici/assistiti':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="gestione-assistiti">
          <GestioneAssistitiPage />
        </RequireAppAccess>
      );
    case 'piani-terapeutici/piani':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="pt-piani">
          <PianiPage />
        </RequireAppAccess>
      );
    case 'piani-terapeutici/assistente-ia':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="pt-assistente-ia">
          <AssistenteIAPage />
        </RequireAppAccess>
      );
    case 'area-sanitaria':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="area-sanitaria">
          <AreaSanitariaPage />
        </RequireAppAccess>
      );
    case 'gestione-terapia':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="gestione-terapie">
          <GestioneTerapiaPage />
        </RequireAppAccess>
      );
    case 'gestione-ordini':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="gestione-ordini">
          <GestioneOrdiniPage />
        </RequireAppAccess>
      );
    case 'gestione-stdp':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="gestione-stdp">
          <GestioneStdpPage />
        </RequireAppAccess>
      );
    case 'gestione-mmg':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="gestione-mmg">
          <GestioneMMGPage />
        </RequireAppAccess>
      );
    case 'contatti-utili':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="info-contatti">
          <ContattiUtiliPage />
        </RequireAppAccess>
      );
    case 'dipendenze/gestione-assistiti':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="gestione-assistiti">
          <GestioneAssistitiPage />
        </RequireAppAccess>
      );
    case 'dipendenze/gestione-strutture':
      return withRouteLoader(
        <RequireAppAccess hubId="dipendenze" appId="gestione-strutture">
          <GestioneStrutturePage />
        </RequireAppAccess>
      );
    case 'applicativi':
      return withRouteLoader(
        <RequireHubAccess hubId="applicativi">
          <ApplicativiHome />
        </RequireHubAccess>
      );
    case 'hr':
      return withRouteLoader(
        <RequireHubAccess hubId="hr">
          <HRHomePage />
        </RequireHubAccess>
      );
    case 'hr/soci-e-dipendenti':
      return withRouteLoader(
        <RequireAppAccess hubId="hr" appId="soci-dipendenti">
          <SociDipendentiPage />
        </RequireAppAccess>
      );
    case 'hr/carica-dati':
      return withRouteLoader(
        <RequireAppAccess hubId="hr" appId="cedolini">
          <HrCaricaDatiPage />
        </RequireAppAccess>
      );
    case 'hr/cedolini':
      return withRouteLoader(
        <RequireAppAccess hubId="hr" appId="cedolini">
          <CedoliniPage />
        </RequireAppAccess>
      );
    case 'hr/inail':
      return withRouteLoader(
        <RequireAppAccess hubId="hr" appId="inail">
          <InailPage />
        </RequireAppAccess>
      );
    case 'hr/parametri':
      return withRouteLoader(
        <RequireAppAccess hubId="hr" appId="parametri">
          <ParametriPage />
        </RequireAppAccess>
      );
    case 'hr/turni':
      return withRouteLoader(
        <RequireAppAccess hubId="hr" appId="turni">
          <TurniPage />
        </RequireAppAccess>
      );
    case 'admin':
      return withRouteLoader(
        <RequirePlatformAdmin>
          <AdminHome />
        </RequirePlatformAdmin>
      );
    case 'admin/access-control':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('access').requiredArea}>
          <AccessControlPage />
        </RequirePlatformAdmin>
      );
    case 'admin/caricamenti':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('uploads').requiredArea}>
          <CaricamentiPage />
        </RequirePlatformAdmin>
      );
    case 'admin/caricamento-dati':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('uploads').requiredArea}>
          <DataImportCenterPage />
        </RequirePlatformAdmin>
      );
    case 'admin/data-imports':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('uploads').requiredArea}>
          <DataImportCenterPage />
        </RequirePlatformAdmin>
      );
    case 'admin/caricamento-gisss':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('uploads').requiredArea}>
          <CaricamentoGISSSPage />
        </RequirePlatformAdmin>
      );
    case 'admin/caricamento-fornitori':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('uploads').requiredArea}>
          <SupplierImportPage />
        </RequirePlatformAdmin>
      );
    case 'admin/utenti':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('users').requiredArea}>
          <SyncUsersPage />
        </RequirePlatformAdmin>
      );
    case 'admin/notifiche':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('notifications').requiredArea}>
          <NotifichePage />
        </RequirePlatformAdmin>
      );
    case 'admin/sicurezza':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('security').requiredArea}>
          <SicurezzaPage />
        </RequirePlatformAdmin>
      );
    case 'admin/manutenzione':
      return withRouteLoader(
        <RequirePlatformAdmin area={getAdminNavigationItem('maintenance').requiredArea}>
          <MaintenanceManagementPage />
        </RequirePlatformAdmin>
      );
    default:
      return <NotFound />;
  }
}
